<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'

const { t } = useI18n()
const tableAdvantages = [
  {
    emphasisTitle: t('components.app_rent_my_motorhome.table_advantages.free_registration.title_emphasis'),
    title: 'components.app_rent_my_motorhome.table_advantages.free_registration.title_dynamic',
    description: t('components.app_rent_my_motorhome.table_advantages.free_registration.description'),
  },
  {
    emphasisTitle: t('components.app_rent_my_motorhome.table_advantages.insurance.title_emphasis'),
    title: 'components.app_rent_my_motorhome.table_advantages.insurance.title_dynamic',
    description: t('components.app_rent_my_motorhome.table_advantages.insurance.description'),
  },
  {
    emphasisTitle: t('components.app_rent_my_motorhome.table_advantages.dashboard.title_emphasis'),
    title: 'components.app_rent_my_motorhome.table_advantages.dashboard.title_dynamic',
    description: t('components.app_rent_my_motorhome.table_advantages.dashboard.description'),
  },
  {
    emphasisTitle: t('components.app_rent_my_motorhome.table_advantages.optimizing.title_emphasis'),
    title: 'components.app_rent_my_motorhome.table_advantages.optimizing.title_dynamic',
    description: t('components.app_rent_my_motorhome.table_advantages.optimizing.description'),
  },
  {
    emphasisTitle: t('components.app_rent_my_motorhome.table_advantages.payment.title_emphasis'),
    title: 'components.app_rent_my_motorhome.table_advantages.payment.title_dynamic',
    description: t('components.app_rent_my_motorhome.table_advantages.payment.description'),
  },
  {
    emphasisTitle: t('components.app_rent_my_motorhome.table_advantages.deposit.title_emphasis'),
    title: 'components.app_rent_my_motorhome.table_advantages.deposit.title_dynamic',
    description: t('components.app_rent_my_motorhome.table_advantages.deposit.description'),
  },
  {
    emphasisTitle: t('components.app_rent_my_motorhome.table_advantages.procedures.title_emphasis'),
    title: 'components.app_rent_my_motorhome.table_advantages.procedures.title_dynamic',
    description: t('components.app_rent_my_motorhome.table_advantages.procedures.description'),
  },
  {
    emphasisTitle: t('components.app_rent_my_motorhome.table_advantages.visibility.title_emphasis'),
    title: 'components.app_rent_my_motorhome.table_advantages.visibility.title_dynamic',
    description: t('components.app_rent_my_motorhome.table_advantages.visibility.description'),
  },
  {
    emphasisTitle: t('components.app_rent_my_motorhome.table_advantages.verification.title_emphasis'),
    title: 'components.app_rent_my_motorhome.table_advantages.verification.title_dynamic',
    description: t('components.app_rent_my_motorhome.table_advantages.verification.description'),
  },
  {
    emphasisTitle: t('components.app_rent_my_motorhome.table_advantages.applications.title_emphasis'),
    title: 'components.app_rent_my_motorhome.table_advantages.applications.title_dynamic',
    description: t('components.app_rent_my_motorhome.table_advantages.applications.description'),
  },
]
const breakpoints = useBreakpoints(breakpointsTailwind)

const maxItemToDisplayOnMobile = 4
const isDisplayMoreMobile = ref(false)

const isDisplayAll = computed(() => {
  return breakpoints.greaterOrEqual('lg').value || isDisplayMoreMobile.value
})

const tableAdvantagesToDisplay = computed(() => {
  if (isDisplayAll.value) {
    return tableAdvantages
  }
  return tableAdvantages.slice(0, maxItemToDisplayOnMobile)
})
</script>

<template>
  <div>
    <div class="ysc-grid-advantages inline-grid gap-5 lg:gap-x-16 lg:items-center justify-center w-full">
      <div class="col-start-2 hidden lg:text-xl lg:block font-semibold">
        {{ $t('components.app_rent_my_motorhome.table_advantages.with_yescapa') }}
      </div>
      <div class="hidden lg:text-xl lg:block">
        {{ $t('components.app_rent_my_motorhome.table_advantages.without_yescapa') }}
      </div>
      <YscDivider class="col-span-3 hidden lg:block" />
      <template
        v-for="advantage in tableAdvantagesToDisplay"
        :key="advantage.title"
      >
        <div class="w-fit mx-auto lg:hidden">
          <span class="bg-gray-100 block rounded-full p-1">
            <YscIconsCheck class="h-6 w-6 text-green-500 [&>path]:stroke-[3] [&>path]:stroke-current" />
          </span>
        </div>
        <div>
          <i18n-t
            :keypath="advantage.title"
            tag="h4"
            scope="global"
            class="lg:text-xl"
          >
            <template #emphasis_string>
              <AppHighlightContent class="font-semibold">
                {{ advantage.emphasisTitle }}
              </AppHighlightContent>
            </template>
          </i18n-t>
          <p class="text-gray-500 text-sm lg:text-base mt-2 prose">
            {{ advantage.description }}
          </p>
        </div>
        <div class="hidden lg:block w-fit mx-auto">
          <span class="bg-gray-100 block rounded-full p-1">
            <YscIconsCheck class="h-6 w-6 text-green-500 [&>path]:stroke-[3] [&>path]:stroke-current" />
          </span>
        </div>
        <div class="hidden lg:block w-fit mx-auto">
          <span class="bg-gray-100 block rounded-full p-1">
            <YscIconsDelete class="h-6 w-6 text-red-400 [&>path]:stroke-[2] [&>path]:stroke-current" />
          </span>
        </div>
        <YscDivider class="col-span-3" />
      </template>
    </div>
    <div
      v-if="isDisplayAll"
      class="text-gray-500 flex-col mt-4 lg:flex mb-3 text-sm"
    >
      <span>
        {{ $t('components.app_rent_my_motorhome.table_advantages.compared') }}
      </span>
      <span>
        {{ $t('components.app_rent_my_motorhome.table_advantages.option') }}
      </span>
    </div>
    <button
      v-if="!isDisplayMoreMobile"
      class="btn btn-secondary mt-4 w-full block lg:hidden"
      @click="isDisplayMoreMobile = true"
    >
      {{ $t('commons.actions.know_more') }}
    </button>
    <button
      v-else
      class="btn btn-tertiary rounded w-full block lg:hidden"
      @click="isDisplayMoreMobile = false"
    >
      {{ $t('commons.actions.display_less') }}
    </button>
  </div>
</template>

<style scoped>
.ysc-grid-advantages {
  grid-template-columns: auto 1fr;
}

@media screen(lg) {
  .ysc-grid-advantages {
    grid-template-columns: 1fr auto auto;
  }
}
</style>
